import type { ReactNode } from 'react'
import type { BookingVehicle, VehicleSelection } from '@/types'

import { UsersRound } from 'lucide-react'
import Eyebrow from '@/components/generals/eyebrow'
import BookingForm from '@/components/booking/booking-form'
import { BOOKING_STEPS } from '@/constants/site'

interface BookingSectionProps {
    vehicles: Array<BookingVehicle>
    selection?: VehicleSelection | null
    lockedVehicle?: BookingVehicle | null
    title?: ReactNode
    intro?: ReactNode
}

export default function BookingSection({ vehicles, selection = null, lockedVehicle = null, title, intro }: BookingSectionProps) {
    return (
        <section id='reservar' aria-labelledby='reservar-titulo' className='relative scroll-mt-18 overflow-hidden bg-foreground py-20 text-background sm:py-24 lg:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute -top-40 -right-40 size-[36rem] rounded-full bg-[radial-gradient(circle,color-mix(in_oklch,var(--champagne)_22%,transparent)_0%,transparent_65%)]' />
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-champagne/40 to-transparent' />

            <div className='relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16 lg:px-8'>
                <div className='lg:sticky lg:top-28 lg:self-start'>
                    <Eyebrow onDark>
                        Reservas
                    </Eyebrow>

                    <h2 id='reservar-titulo' className='mt-6 font-heading text-4xl leading-[1.1] text-balance sm:text-5xl'>
                        {title ?? (
                            <>
                                Reserva tu vehículo <em className='text-champagne italic'>con chófer</em>
                            </>
                        )}
                    </h2>

                    <p className='mt-5 max-w-md text-base leading-relaxed text-background/70'>
                        {intro ?? 'Cuéntanos cuándo y dónde te recogemos. Te confirmaremos la disponibilidad y el precio final antes de cerrar nada.'}
                    </p>

                    <ol className='mt-10 space-y-6'>
                        {BOOKING_STEPS.map((step, index) => (
                            <li key={step.title} className='flex gap-4'>
                                <span aria-hidden='true' className='flex size-10 shrink-0 items-center justify-center rounded-full border border-champagne/40 font-heading text-lg text-champagne'>
                                    {index + 1}
                                </span>

                                <div>
                                    <p className='font-semibold text-background'>
                                        {step.title}
                                    </p>

                                    <p className='mt-1 text-sm leading-relaxed text-background/60'>
                                        {step.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className='mt-10 flex items-start gap-3 rounded-2xl border border-background/10 bg-background/5 p-4 text-sm leading-relaxed text-background/70'>
                        <UsersRound className='mt-0.5 size-5 shrink-0 text-champagne' aria-hidden='true' />

                        <p>
                            ¿Sois un grupo grande? Indícalo en los comentarios y te propondremos combinar varios vehículos para que viajéis juntos.
                        </p>
                    </div>
                </div>

                <BookingForm vehicles={vehicles} selection={selection} lockedVehicle={lockedVehicle} />
            </div>
        </section>
    )
}
